import { EditablePreviewWorld, Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import {
  findStateByName,
  matchesTemplateOrClone,
  nextTempId,
  overlapsAabb,
} from './sharedHelpers'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS02（ボールをゴールへ転がせ）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS02.rbxl）のギミックを移植する:
 * - Goal/GoalScript（追従ループ）: 毎フレーム、Goal の幅と X 位置を
 *   ②ひだりゴール と ③みぎゴール の間に張り直す（学習者がゴールポストを
 *   動かすとゴール板も伸縮・移動する）。どちらかのポストが workspace から
 *   消えたら Goal 自身を destroy してループを終える。
 * - Goal/GoalScript（接触判定）: ①ボール が Goal に触れたら（未判定のとき
 *   だけ）Winner を鳴らし、ServerStorage の Athle（アスレチックコース 8 パーツ）
 *   を workspace へ出現させる。GUI は「ゴーーール！！！」を 5 秒表示。
 * - NotGoalCreateScript + NotGoalScript: NotGoal フォルダの透明な壁 3 枚
 *   （NotGoal / NotGoal#2 / NotGoal#3）に ①ボール が触れたら（未判定のとき
 *   だけ）Game Over を鳴らし、GUI は「ざんねん・・・」を 3 秒表示。
 * - StarterGui/GUILocalScript: 上記の表示・非表示タイミング。
 *
 * プレビューでの近似・意図的な差異:
 * - ボールと判定板の接触は、Lua の Touched の代わりに AABB 重なりの
 *   毎フレーム監視で判定する（オブジェクト同士の Touched はランタイムの
 *   接触イベントに乗らないため。rjf03/rjf12 と同方式）。
 * - Lua の hit.Name == "①ボール" は Roblox の Clone()（同名複製）にも反応
 *   するため、プレビューの control_clone 命名（元名#cloneN）も対象にする
 *   （rjf09 と同じ判定をミッションローカルに持つ）。
 * - 効果音はゴール（Winner）に成功音、失敗判定（Game Over）に失敗音を実音源で
 *   鳴らす。
 * - BGM はシーン JSON の bgm 機構の担当（ビヘイビア対象外）。実機の 'Tip Top'
 *   はプロダクト指定の Sunrise（bgm-overrides.json）へ差し替え済み。
 * - Athle は実機では ServerStorage のフォルダだが、シーン JSON には
 *   ServerStorage が含まれないため、rbxl の実測値（位置・寸法・色・素材）を
 *   ここに持ちゴール時に addObject で出現させる。
 */

/** 学習者が転がすボールのオブジェクト名（Lua: hit.Name == "①ボール"）。 */
const BALL_NAME = '①ボール'

/** ゴール板・ゴールポストのオブジェクト名。 */
const GOAL_NAME = 'Goal'
const LEFT_POST_NAME = '②ひだりゴール'
const RIGHT_POST_NAME = '③みぎゴール'

/** 失敗判定の透明な壁（NotGoal フォルダ 3 枚。シーン変換で連番化済み）。 */
const NOT_GOAL_NAMES: ReadonlyArray<string> = [
  'NotGoal',
  'NotGoal#2',
  'NotGoal#3',
]

/** ゴール表示の秒数（Lua: GUILocalScript goal() の wait(5)）。 */
const GOAL_MESSAGE_SECONDS = 5

/** 失敗表示の秒数（Lua: GUILocalScript notGoal() の wait(3)）。 */
const NOT_GOAL_MESSAGE_SECONDS = 3

/** 追従ループの「変化なし」とみなす許容差（不要な再描画通知を避ける）。 */
const FOLLOW_EPSILON = 1e-6

/** ゴール GUI の実機デザイン（ScreenGui/GoalTextLabel の実測値）。 */
const GOAL_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#AA0000',
  border: '#FF5500',
  borderWidth: 5,
  wide: true,
}

/** 失敗 GUI の実機デザイン（ScreenGui/NotGoalTextLabel の実測値、枠なし）。 */
const NOT_GOAL_HUD_STYLE: HudStyle = {
  background: '#AAFFFF',
  color: '#000000',
  wide: true,
}

/** ゴール時に出現するアスレチックの色・素材（ServerStorage/Athle の実測値）。 */
const ATHLE_COLOR = '#111111'
const ATHLE_MATERIAL = 'Basalt'

/**
 * ゴール時に出現するアスレチックコース（Lua: storage.Athle.Parent = workspace）。
 * 実機の ServerStorage/Athle フォルダ配下 8 パーツの実測値
 * （いずれも Anchored・CanCollide=true）。
 */
const ATHLE_PARTS: ReadonlyArray<{
  readonly name: string
  readonly position: Vec3
  readonly size: Vec3
}> = [
  {
    name: 'Part10',
    position: { x: -20.5, y: 21.5, z: 2 },
    size: { x: 1, y: 5, z: 10 },
  },
  {
    name: 'Part2',
    position: { x: 20.5, y: 2.5, z: -14.5 },
    size: { x: 1, y: 5, z: 11 },
  },
  {
    name: 'Part3',
    position: { x: 20.5, y: 7.5, z: 3.5 },
    size: { x: 1, y: 5, z: 11 },
  },
  {
    name: 'Part4',
    position: { x: 20.5, y: 14.5, z: 15.5 },
    size: { x: 1, y: 5, z: 7 },
  },
  {
    name: 'Part6',
    position: { x: 14, y: 14.5, z: 20.5 },
    size: { x: 10, y: 5, z: 1 },
  },
  {
    name: 'Part7',
    position: { x: -2, y: 14.5, z: 20.5 },
    size: { x: 10, y: 5, z: 1 },
  },
  {
    name: 'Part8',
    position: { x: -15, y: 14.5, z: 20.5 },
    size: { x: 10, y: 5, z: 1 },
  },
  {
    name: 'Part9',
    position: { x: -20.5, y: 16.5, z: 15 },
    size: { x: 1, y: 5, z: 10 },
  },
]

export const createRjs02Behaviors = (): MissionBehaviors => {
  /**
   * Goal の追従ループ（Lua: GoalScript 後半の while）。毎フレーム、ポスト
   * 2 本の X 間隔へ幅を、中点へ X 位置を合わせる。どちらかのポストが
   * 消えたら Goal を destroy して終わる（以降ゴール判定も成立しない）。
   */
  const followGoal: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    const initial = findStateByName(world, GOAL_NAME)
    if (!initial) {
      return
    }
    const goalId = initial.id
    while (!ctx.signal.aborted) {
      const left = findStateByName(world, LEFT_POST_NAME)
      const right = findStateByName(world, RIGHT_POST_NAME)
      if (!left || !right) {
        // Lua: while 条件が崩れたら goal:Destroy()。
        if (world.exists(goalId)) {
          world.destroy(goalId)
        }
        return
      }
      const goal = world.getState().get(goalId)
      if (!goal) {
        return
      }
      const targetSizeX = Math.abs(left.position.x - right.position.x)
      const targetCenterX = (left.position.x + right.position.x) / 2
      // Lua は毎フレーム無条件で代入するが、値が変わらない代入は購読側の
      // 再描画を無駄に起こすため変化があるときだけ反映する。
      if (Math.abs(goal.size.x - targetSizeX) > FOLLOW_EPSILON) {
        world.setSize(goalId, {
          x: targetSizeX,
          y: goal.size.y,
          z: goal.size.z,
        })
      }
      if (Math.abs(goal.position.x - targetCenterX) > FOLLOW_EPSILON) {
        world.setPosition(goalId, {
          x: targetCenterX,
          y: goal.position.y,
          z: goal.position.z,
        })
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  /** アスレチックコースを出現させる（Lua: storage.Athle.Parent = workspace）。 */
  const spawnAthleCourse = (world: EditablePreviewWorld): void => {
    for (const part of ATHLE_PARTS) {
      world.addObject({
        id: nextTempId(world, 'rjs02_athle'),
        name: part.name,
        position: { ...part.position },
        size: { ...part.size },
        color: ATHLE_COLOR,
        material: ATHLE_MATERIAL,
        isStatic: true,
        canCollide: true,
        canTouch: true,
      })
    }
  }

  /**
   * ボール監視ループ（GoalScript 前半 + NotGoalScript ×3 + GUILocalScript の
   * 統合）。最初に成立した判定だけが有効（Lua の isGoal/isNotGoal 相互ガード）
   * で、以降は何も起きないためループ自体を終える。
   */
  const watchBall: BehaviorHandler = async (ctx) => {
    const { world } = ctx
    while (!ctx.signal.aborted) {
      // ボール本体（①ボール）と control_clone（①ボール#cloneN）の両方が対象。
      // 実機の Clone() は同名複製のため hit.Name 判定がクローンにも効く。
      const balls = [...world.getState().values()].filter((state) =>
        matchesTemplateOrClone(state.name, BALL_NAME),
      )
      for (const ball of balls) {
        // ゴール判定（Lua: GoalScript の onTouch）。
        const goal = findStateByName(world, GOAL_NAME)
        if (
          goal &&
          overlapsAabb(ball.position, ball.size, goal.position, goal.size)
        ) {
          playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
          spawnAthleCourse(world)
          ctx.setHudText?.('ゴーーール！！！', GOAL_HUD_STYLE)
          await ctx.wait(GOAL_MESSAGE_SECONDS)
          if (!ctx.signal.aborted) {
            ctx.setHudText?.(null)
          }
          return
        }
        // 失敗判定（Lua: NotGoalScript の onTouch ×3）。
        for (const notGoalName of NOT_GOAL_NAMES) {
          const notGoal = findStateByName(world, notGoalName)
          if (
            notGoal &&
            overlapsAabb(
              ball.position,
              ball.size,
              notGoal.position,
              notGoal.size,
            )
          ) {
            playSample(
              FAIL_GAMEOVER_SFX.url,
              FAIL_GAMEOVER_SFX.volume,
              ctx.signal,
            )
            ctx.setHudText?.('ざんねん・・・', NOT_GOAL_HUD_STYLE)
            await ctx.wait(NOT_GOAL_MESSAGE_SECONDS)
            if (!ctx.signal.aborted) {
              ctx.setHudText?.(null)
            }
            return
          }
        }
      }
      await ctx.wait(FRAME_SECONDS)
    }
  }

  return { onPlay: [followGoal, watchBall] }
}
